import type { TopFilter } from './caseTaxonomyFilter';
import { AREA_OF_LAW_KEY_TO_LABEL } from './areaOfLaw';
import { CIVIL_CASE_TAXONOMY_CONFIG } from './civilTaxonomy';
import {
  CRIMINAL_CASE_TAXONOMY_CONFIG,
  getCriminalPartyLabels,
  getCriminalProceedingConfig,
} from './criminalTaxonomy';
import { FAMILY_CASE_TAXONOMY_CONFIG } from './familyTaxonomy';
import {
  getWritLegacyMatterType,
  WRIT_CASE_TAXONOMY_CONFIG,
  WRIT_LEGACY_MATTER_TYPES,
} from './writTaxonomy';
import { RENT_CASE_TAXONOMY_CONFIG } from './rentTaxonomy';
import { REVENUE_LAND_CASE_TAXONOMY_CONFIG } from './revenueLandTaxonomy';
import { BANKING_CASE_TAXONOMY_CONFIG } from './bankingTaxonomy';
import { CONSUMER_CASE_TAXONOMY_CONFIG } from './consumerTaxonomy';
import { NAB_CASE_TAXONOMY_CONFIG } from './nabTaxonomy';
import { FIA_FEDERAL_OFFENCES_CASE_TAXONOMY_CONFIG } from './fiaTaxonomy';
import { SUPREME_COURT_CASE_TAXONOMY_CONFIG } from './supremeCourtTaxonomy';
import { getConsumerMatterStages } from './consumerTaxonomy';

export interface CourtOption {
  id: string;
  label: string;
  description?: string;
  recommended?: boolean;
  recommendedIf?: (value: number) => boolean;
}

export interface CaseMatterType {
  id: string;
  label: string;
  shortLabel?: string;
  description?: string;
  legalBasis?: string | string[];
  courtOptions?: CourtOption[];
  sideOptions?: string[];
  documentTabs?: string[];
  defaultStages?: string[];
  subtypes?: string[];
  importantNotes?: string;
  aiContextHints?: string;
  [key: string]: unknown;
}

export interface CaseTaxonomyConfig {
  id: string;
  label: string;
  description?: string;
  active?: boolean;
  types: CaseMatterType[];
}

/** Stored on Firestore cases as `caseCategory`. */
export type CaseCategoryLabel =
  | 'Civil'
  | 'Criminal'
  | 'Family'
  | 'Constitutional / Writ'
  | 'Rent / Tenancy'
  | 'Revenue / Land'
  | 'Banking / Financial Recovery'
  | 'Consumer Protection'
  | 'NAB / Accountability'
  | 'FIA / Cybercrime / Federal Offences'
  | 'Supreme Court';

export type AreaOfLaw =
  | 'Civil'
  | 'Criminal'
  | 'Family'
  | 'Writ'
  | 'Rent'
  | 'Revenue'
  | 'Banking'
  | 'Consumer'
  | 'NAB'
  | 'FIA'
  | 'SupremeCourt';

export const AREA_OF_LAW_TO_CASE_CATEGORY: Record<AreaOfLaw, CaseCategoryLabel> = {
  Civil: 'Civil',
  Criminal: 'Criminal',
  Family: 'Family',
  Writ: 'Constitutional / Writ',
  Rent: 'Rent / Tenancy',
  Revenue: 'Revenue / Land',
  Banking: 'Banking / Financial Recovery',
  Consumer: 'Consumer Protection',
  NAB: 'NAB / Accountability',
  FIA: 'FIA / Cybercrime / Federal Offences',
  SupremeCourt: 'Supreme Court',
};

export const CASE_CATEGORY_TO_TOP_FILTER: Record<CaseCategoryLabel, TopFilter> = {
  Civil: 'Civil',
  Criminal: 'Criminal',
  Family: 'Family',
  'Constitutional / Writ': 'Writ',
  'Rent / Tenancy': 'Rent',
  'Revenue / Land': 'Revenue',
  'Banking / Financial Recovery': 'Banking',
  'Consumer Protection': 'Consumer',
  'NAB / Accountability': 'NAB',
  'FIA / Cybercrime / Federal Offences': 'FIA',
  'Supreme Court': 'SupremeCourt',
};

export const TOP_FILTER_TO_CASE_CATEGORY: Partial<Record<TopFilter, CaseCategoryLabel>> = {
  Civil: 'Civil',
  Criminal: 'Criminal',
  Family: 'Family',
  Writ: 'Constitutional / Writ',
  Rent: 'Rent / Tenancy',
  Revenue: 'Revenue / Land',
  Banking: 'Banking / Financial Recovery',
  Consumer: 'Consumer Protection',
  NAB: 'NAB / Accountability',
  FIA: 'FIA / Cybercrime / Federal Offences',
  SupremeCourt: 'Supreme Court',
};

export const ALL_CASE_TAXONOMIES: CaseTaxonomyConfig[] = [
  CIVIL_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  CRIMINAL_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  FAMILY_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  WRIT_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  RENT_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  REVENUE_LAND_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  BANKING_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  CONSUMER_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  NAB_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  FIA_FEDERAL_OFFENCES_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
  SUPREME_COURT_CASE_TAXONOMY_CONFIG as unknown as CaseTaxonomyConfig,
];

const CATEGORY_BY_LABEL = new Map<string, CaseTaxonomyConfig>(
  ALL_CASE_TAXONOMIES.map((c) => [c.label, c]),
);

const MATTER_TYPE_INDEX = new Map<string, { category: CaseCategoryLabel; type: CaseMatterType }>();

for (const config of ALL_CASE_TAXONOMIES) {
  const categoryLabel = config.label as CaseCategoryLabel;
  for (const type of config.types) {
    MATTER_TYPE_INDEX.set(type.id, { category: categoryLabel, type });
  }
}

/** Generic court presets for fully custom cases (area/matter not in taxonomy). */
export const CUSTOM_CASE_COURT_OPTIONS: CourtOption[] = [
  { id: 'district_court', label: 'District Court', description: 'Civil or criminal district forum' },
  { id: 'high_court', label: 'High Court', description: '' },
  { id: 'magistrate', label: 'Magistrate / Sessions Court', description: '' },
];

/** Default workflow stages when no taxonomy match exists. */
export const CUSTOM_CASE_DEFAULT_STAGES: string[] = [
  'Case Preparation',
  'Filing',
  'Proceedings',
  'Order / Judgment',
  'Closed',
];

/** Catch-all matter type id per standard area (for "my matter isn't listed"). */
export const OTHER_CATCHALL_BY_AREA: Record<AreaOfLaw, string> = {
  Civil: 'Other Civil Matter',
  Criminal: 'Other Criminal Matter',
  Family: 'Other Family Matter',
  Writ: 'Other Constitutional Matter',
  Rent: 'Other Rent / Tenancy Matter',
  Revenue: 'Other Revenue / Land Matter',
  Banking: 'Other Banking / Financial Matter',
  Consumer: 'Other Consumer Matter',
  NAB: 'Other NAB / Accountability Matter',
  FIA: 'Other FIA / Federal Offence Matter',
  SupremeCourt: 'Other Supreme Court Matter',
};

/** Closest standard area choices for fully custom cases. */
export const CUSTOM_CLOSEST_AREA_CHOICES: {
  id: AreaOfLaw | '';
  label: string;
  description?: string;
}[] = [
  { id: '', label: 'None / not sure', description: 'Use generic stages and document templates' },
  { id: 'Civil', label: 'Civil' },
  { id: 'Criminal', label: 'Criminal' },
  { id: 'Family', label: 'Family' },
  { id: 'Writ', label: 'Constitutional / Writ' },
  { id: 'Rent', label: 'Rent / Tenancy' },
  { id: 'Revenue', label: 'Revenue / Land' },
  { id: 'Banking', label: 'Banking / Financial Recovery' },
  { id: 'Consumer', label: 'Consumer Protection' },
  { id: 'NAB', label: 'NAB / Accountability' },
  { id: 'FIA', label: 'FIA / Federal Offences' },
  { id: 'SupremeCourt', label: 'Supreme Court' },
];

/** Sentinel value for the custom / other option in matter type pickers. */
export const CUSTOM_MATTER_TYPE_OPTION = '__custom__';

/** Catch-all matter types for flows that previously used legacy `Other`. */
export const OTHER_MATTER_TYPES: { areaOfLaw: AreaOfLaw; matterTypeId: string; label: string }[] = [
  { areaOfLaw: 'Civil', matterTypeId: 'Other Civil Matter', label: 'Other Civil Matter' },
  { areaOfLaw: 'Criminal', matterTypeId: 'Other Criminal Matter', label: 'Other Criminal Matter' },
  { areaOfLaw: 'Family', matterTypeId: 'Other Family Matter', label: 'Other Family Matter' },
  { areaOfLaw: 'Writ', matterTypeId: 'Other Constitutional Matter', label: 'Other Constitutional Matter' },
  { areaOfLaw: 'Rent', matterTypeId: 'Other Rent / Tenancy Matter', label: 'Other Rent / Tenancy Matter' },
  { areaOfLaw: 'Revenue', matterTypeId: 'Other Revenue / Land Matter', label: 'Other Revenue / Land Matter' },
  { areaOfLaw: 'Banking', matterTypeId: 'Other Banking / Financial Matter', label: 'Other Banking / Financial Matter' },
  { areaOfLaw: 'Consumer', matterTypeId: 'Other Consumer Matter', label: 'Other Consumer Matter' },
  { areaOfLaw: 'NAB', matterTypeId: 'Other NAB / Accountability Matter', label: 'Other NAB / Accountability Matter' },
  { areaOfLaw: 'FIA', matterTypeId: 'Other FIA / Federal Offence Matter', label: 'Other FIA / Federal Offence Matter' },
  { areaOfLaw: 'SupremeCourt', matterTypeId: 'Other Supreme Court Matter', label: 'Other Supreme Court Matter' },
];

/** Legacy CASE_TYPES keys → taxonomy matter type id */
const LEGACY_MATTER_TYPE_ALIASES: Record<string, string> = {
  'Bail Application': 'Post-Arrest Bail (BA)',
  Bail: 'Post-Arrest Bail (BA)',
  'Protective Bail': 'Protective / Transit Bail',
  'Criminal Trial': 'Sessions Trial',
  'Criminal Appeal': 'Appeal Against Conviction',
  'Criminal Revision': 'Revision Against Order',
  'Criminal Miscellaneous Application': 'Other Criminal Application',
  'Private Complaint': 'PPC Complaint',
  'Quashment Petition': 'Quashment of FIR',
  'Other Criminal Matter': 'Custom Criminal Matter',
  Other: 'Other Civil Matter',
};

export function getTaxonomyConfigByCategory(caseCategory: string): CaseTaxonomyConfig | undefined {
  return CATEGORY_BY_LABEL.get(caseCategory);
}

export function getTaxonomyConfigForTopFilter(topFilter: TopFilter): CaseTaxonomyConfig | undefined {
  const cat = TOP_FILTER_TO_CASE_CATEGORY[topFilter];
  return cat ? getTaxonomyConfigByCategory(cat) : undefined;
}

export function getMatterType(
  caseCategory: string,
  matterTypeId: string,
): CaseMatterType | undefined {
  const config = getTaxonomyConfigByCategory(caseCategory);
  return config?.types.find((t) => t.id === matterTypeId);
}

export function resolveMatterTypeId(raw: string | undefined): string {
  if (!raw?.trim()) return '';
  const trimmed = raw.trim();
  if (MATTER_TYPE_INDEX.has(trimmed)) return trimmed;
  return LEGACY_MATTER_TYPE_ALIASES[trimmed] ?? trimmed;
}

export interface CaseTaxonomyContextInput {
  caseCategory?: string;
  matterType?: string;
  caseType?: string;
  category?: string;
  criminalCategory?: string;
  proceedingType?: string;
  party1Label?: string;
  party2Label?: string;
  isCustomCase?: boolean;
  closestStandardArea?: string;
}

export interface PartySideLabels {
  party1Label: string;
  party2Label: string;
  party1Plural: string;
  party2Plural: string;
}

const DEFAULT_PARTY_LABELS: PartySideLabels = {
  party1Label: 'Plaintiff',
  party2Label: 'Defendant',
  party1Plural: 'Plaintiffs',
  party2Plural: 'Defendants',
};

export function inferCategoryFromMatterType(matterTypeId: string): CaseCategoryLabel | undefined {
  const resolved = resolveMatterTypeId(matterTypeId);
  return MATTER_TYPE_INDEX.get(resolved)?.category;
}

export function getMatterTypeForCase(
  caseData: CaseTaxonomyContextInput,
): CaseMatterType | undefined {
  const matterId = resolveMatterTypeId(
    caseData.matterType || caseData.caseType || caseData.category,
  );
  if (!matterId) return undefined;

  const fromIndex = MATTER_TYPE_INDEX.get(matterId);
  if (fromIndex) return fromIndex.type;

  const legacyWrit = getWritLegacyMatterType(matterId);
  if (legacyWrit) return legacyWrit;

  // Renamed catch-all
  if (matterId === 'Other Constitutional / Writ Matter') {
    return getMatterType('Constitutional / Writ', 'Other Constitutional Matter');
  }

  const cat = caseData.caseCategory;
  if (cat) return getMatterType(cat, matterId);
  return undefined;
}

/** Legacy writ matter type ids (for import normalization and filters). */
export function getWritLegacyMatterTypeIds(): string[] {
  return WRIT_LEGACY_MATTER_TYPES.map((t) => t.id);
}

export function getAllMatterTypeIds(): string[] {
  return [...MATTER_TYPE_INDEX.keys()];
}

/** Human-readable label for a matter type id (taxonomy or legacy alias). */
export function getMatterTypeDisplayLabel(matterTypeId: string): string {
  const resolved = resolveMatterTypeId(matterTypeId);
  return MATTER_TYPE_INDEX.get(resolved)?.type.label ?? matterTypeId;
}

export function listMatterTypesForCategory(caseCategory: string): CaseMatterType[] {
  return getTaxonomyConfigByCategory(caseCategory)?.types ?? [];
}

export function getDefaultStages(caseData: CaseTaxonomyContextInput): string[] {
  if (caseData.isCustomCase) {
    const closest = caseData.closestStandardArea as AreaOfLaw | undefined;
    if (closest && OTHER_CATCHALL_BY_AREA[closest]) {
      const cat = AREA_OF_LAW_TO_CASE_CATEGORY[closest];
      const otherType = getMatterType(cat, OTHER_CATCHALL_BY_AREA[closest]);
      if (otherType?.defaultStages?.length) {
        return [...otherType.defaultStages];
      }
    }
    return [...CUSTOM_CASE_DEFAULT_STAGES];
  }

  const matterId = resolveMatterTypeId(
    caseData.matterType || caseData.caseType || caseData.category,
  );
  const subtype = (caseData as { caseSubtype?: string }).caseSubtype;
  const consumerStages = getConsumerMatterStages(matterId, subtype);
  if (consumerStages) return consumerStages;

  const type = getMatterTypeForCase(caseData);
  if (type?.defaultStages?.length) {
    return [...type.defaultStages];
  }

  const cat = caseData.caseCategory || caseData.category;
  if (cat && !CATEGORY_BY_LABEL.has(cat)) {
    return [...CUSTOM_CASE_DEFAULT_STAGES];
  }

  return [];
}

export function getCourtLabels(caseData: CaseTaxonomyContextInput): string[] {
  const type = getMatterTypeForCase(caseData);
  return (type?.courtOptions ?? []).map((c) => c.label);
}

function makePlural(label: string): string {
  if (label.endsWith('y') && !label.endsWith('ay') && !label.endsWith('ey')) {
    return `${label.slice(0, -1)}ies`;
  }
  if (label.endsWith('s') || label.endsWith('x') || label.endsWith('z')) {
    return `${label}es`;
  }
  return `${label}s`;
}

export function getPartyLabels(caseData: CaseTaxonomyContextInput): PartySideLabels {
  const type = getMatterTypeForCase(caseData);
  const side0 = type?.sideOptions?.[0] ?? DEFAULT_PARTY_LABELS.party1Label;
  const side1 = type?.sideOptions?.[1] ?? DEFAULT_PARTY_LABELS.party2Label;
  return {
    party1Label: side0,
    party2Label: side1,
    party1Plural: makePlural(side0),
    party2Plural: makePlural(side1),
  };
}

/** Resolve party side labels from stored case fields or matter taxonomy. */
export function resolvePartyLabelsForCase(
  caseData: CaseTaxonomyContextInput | null | undefined,
): PartySideLabels {
  if (!caseData) return DEFAULT_PARTY_LABELS;

  const stored1 = caseData.party1Label?.trim();
  const stored2 = caseData.party2Label?.trim();
  if (stored1 && stored2) {
    return {
      party1Label: stored1,
      party2Label: stored2,
      party1Plural: makePlural(stored1),
      party2Plural: makePlural(stored2),
    };
  }

  const cat = caseData.caseCategory || caseData.category;
  if (cat === 'Criminal') {
    const categoryId = String(caseData.criminalCategory || '');
    const proceedingId = String(
      caseData.proceedingType || caseData.matterType || caseData.caseType || '',
    );
    const config = getCriminalProceedingConfig(categoryId, proceedingId);
    if (config) {
      const labels = getCriminalPartyLabels(config);
      return {
        party1Label: labels.party1Label,
        party2Label: labels.party2Label,
        party1Plural: labels.party1Plural,
        party2Plural: labels.party2Plural,
      };
    }
  }

  return getPartyLabels(caseData);
}

function getFirstPartyNameFromLine(line: string): string {
  const cleanLine = line.replace(/^\d+\.\s*/, '');
  const match = cleanLine.split(/(S\/o|D\/o|W\/o|R\/o)/i);
  if (match.length > 0 && match[0].trim()) {
    return match[0].trim();
  }
  return cleanLine.trim();
}

/** Auto-generate "Name Vs. Name" title; blank sides use taxonomy side labels. */
export function generateCaseTitleFromPartyText(
  pText: string,
  dText: string,
  labels?: Pick<PartySideLabels, 'party1Label' | 'party2Label'>,
): string {
  const pLines = pText.split('\n').filter((l) => l.trim().length > 0);
  const dLines = dText.split('\n').filter((l) => l.trim().length > 0);
  const party1Fallback = labels?.party1Label || DEFAULT_PARTY_LABELS.party1Label;
  const party2Fallback = labels?.party2Label || DEFAULT_PARTY_LABELS.party2Label;

  const pName = pLines.length > 0 ? getFirstPartyNameFromLine(pLines[0]) : party1Fallback;
  const dName = dLines.length > 0 ? getFirstPartyNameFromLine(dLines[0]) : party2Fallback;

  const pSuffix = pLines.length === 2 ? ' and another' : pLines.length > 2 ? ' and others' : '';
  const dSuffix = dLines.length === 2 ? ' and another' : dLines.length > 2 ? ' and others' : '';

  return `${pName}${pSuffix}  Vs.  ${dName}${dSuffix}`;
}

export function resolveStartingStageFromTaxonomy(
  caseData: CaseTaxonomyContextInput,
  representingSide?: string,
): string {
  const stages = getDefaultStages(caseData);
  if (stages.length === 0) return 'Case created';

  const side = representingSide?.toLowerCase() ?? '';
  const isDefendantSide =
    side === 'defendant' ||
    side.includes('respondent') ||
    side.includes('judgment debtor') ||
    side.includes('opposing');

  if (isDefendantSide) {
    const mid =
      stages.find((s) =>
        /appearance|reply|objection|notice|written|challan|report|comments|parawise/i.test(s),
      ) ?? stages[Math.min(3, stages.length - 1)];
    return mid;
  }
  return stages[0];
}

export interface TemplatePickerMatterType {
  id: string;
  description: string;
  stages: string[];
}

export interface TemplatePickerAreaGroup {
  areaKey: AreaOfLaw;
  areaLabel: string;
  categoryDescription?: string;
  matterTypes: TemplatePickerMatterType[];
}

export function getTemplatePickerMatterTypesByAreaOfLaw(): TemplatePickerAreaGroup[] {
  return ALL_CASE_TAXONOMIES.map((config) => {
    const categoryLabel = config.label as CaseCategoryLabel;
    const areaKey = CASE_CATEGORY_TO_TOP_FILTER[categoryLabel];
    return {
      areaKey,
      areaLabel: AREA_OF_LAW_KEY_TO_LABEL[areaKey] ?? config.label,
      categoryDescription: config.description,
      matterTypes: config.types.map((t) => ({
        id: t.id,
        description: t.description ?? '',
        stages: t.defaultStages ?? [],
      })),
    };
  });
}

export function getTemplatePickerMatterTypes(): TemplatePickerMatterType[] {
  return getTemplatePickerMatterTypesByAreaOfLaw().flatMap((group) => group.matterTypes);
}

export function inferCaseCategoryForCase(caseData: CaseTaxonomyContextInput): string | undefined {
  if (caseData.caseCategory?.trim()) return caseData.caseCategory.trim();
  const matterId = resolveMatterTypeId(
    caseData.matterType || caseData.caseType || caseData.category,
  );
  if (matterId) return inferCategoryFromMatterType(matterId);
  return undefined;
}
